import * as React from "react";
import {CardBoxSmall} from "../../../components/CardBox";
import {Title} from "../../../components/Title";
import {Legend, LegendItem, LegendLiElement} from "../../../components/charts/Legend";
import {ColorUtils} from "../../../components/Utils/color/ColorUtils";
import {Loader} from "../../../components/Utils/loader/Loader";
import {StatisticsUtils} from "../../../components/Utils/statistics/StatisticsUtils";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {CategoriesColors, NotAtManufacturersWidgetTexts, WidgetResponse} from "../../DashboardContainer";
import {DashboardRequestsData} from "../../data/DashboardData";
import {JSONMapper} from "../../data/JSONMapper";
import {PieChartWithLabels} from "../charts/PieChartWithLabels";
import {FormatLocaleNumber} from "../DashboardUtils";
import {AssetBaseDashboardWidget} from "./AssetBaseDashboardWidget";
import {LegendTooltip, LegendTooltipConfig} from "./LegendTooltip";
import {REGISTERED_LOCATIONS_IDS, SiteTypes} from "./MarketWidget";
import {RestDataLoader} from "./RestDataLoader";
import "./style/Legend.scss";
import "./style/NotAtManufacturersWidget.scss";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {WidgetBadge, WidgetBadgeType} from "../DashboardWidget";
import AutoSizer from "react-virtualized-auto-sizer";

declare var AppTranslations: AppTranslations;

interface NotAtManufacturersWidgetProps {
    data: WidgetResponse;
    texts: NotAtManufacturersWidgetTexts;
    withTelemetryDevice: boolean;
    colorGenerator: ColorUtils;
}

interface WidgetState {
    ready: boolean;
    error: boolean;
    cardsData: CardsData;
    equipmentByType: DashboardRequestsData.EquipmentByType[];
    selectedItemName: string;
    tooltipConfig: LegendTooltipConfig;
}

export interface CardsData {
    equipmentTotal: number;
    newEquipmentCount: number;
    onMarketEquipmentCount: number;
    equipmentInUnknownPosCount: number;
}

export enum TelemetryStatus {
    ACTIVATED = "activated",
    DISABLED = "disabled",
}

export class NotAtManufacturersWidget extends React.Component<NotAtManufacturersWidgetProps, WidgetState> {
    constructor(props: NotAtManufacturersWidgetProps) {
        super(props);
        this.state = {
            ready: false,
            error: false,
            cardsData: null,
            equipmentByType: null,
            selectedItemName: null,
            tooltipConfig: {visible: false},
        };
        this.setSelectedItem = this.setSelectedItem.bind(this);
    }

    public setSelectedItem(itemName: string, e): void {
        const target: HTMLLIElement = e ? e.currentTarget : null;
        this.setState({selectedItemName: itemName}, () => {
            if (itemName !== null && itemName.indexOf(this.props.texts.others) > -1) {
                if (target) {
                    this.setState({
                        tooltipConfig: {
                            visible: true,
                            offSetX: target.offsetLeft,
                            offSetY: target.offsetTop + target.offsetHeight,
                            width: target.offsetWidth,
                            data: [],
                            father: target,
                        },
                    });
                }
            }
        });
    }

    public ready() {
        if (this.state.cardsData != null && this.state.equipmentByType != null) {
            this.setState({ready: true});
        }
    }

    public componentDidMount() {
        RestDataLoader.loadData(
            "statistics/equipment/counting/placement",
            {
                placements: "warehouse,market",
                withTelemetry: this.props.withTelemetryDevice,
            },
            (data: any) => {
                this.setState({cardsData: JSONMapper.jsonEquipmentDealersCardsDataMapper(data.list)});
                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );
        RestDataLoader.loadData(
            "statistics/equipment/counting/type",
            {
                placements: "warehouse,market",
                withTelemetry: this.props.withTelemetryDevice,
            },
            (data: any) => {
                this.setState({equipmentByType: JSONMapper.jsonEquipmentByTypeMapper(data.list)});
                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );
    }

    public render() {
        if (this.state.error) {
            return null;
        }
        let content = <Loader/>;
        if (!this.state.ready) {
            return (
                <div className="dealers-widget">
                    {content}
                </div>
            );
        }
        const equipmentByTypeData = this.state.equipmentByType.map(
            (item, i) => {
                return {
                    id: `${i}`,
                    code: item.typeGuid,
                    label: item.typeName,
                    frequency: item.equipmentCount,
                    icon: item.typeLogo,
                    color: item.typeColor || this.props.colorGenerator.getMappedColor(
                        CategoriesColors.COMPONENT_CATEGORIES,
                        item.typeGuid,
                    ),
                };
            });
        const preparedData = PieChartWithLabels.prepareData(
            equipmentByTypeData,
            6,
            "desc",
            {others: this.props.texts.others},
            undefined,
        );
        let total = 0;
        const legendItems: LegendItem[] = preparedData.map((pd) => {
            total += pd.frequency;
            return {
                color: pd.color,
                name: pd.label,
                value: pd.frequency,
                icon: pd.icon,
            };
        });
        legendItems.map((li) => li.percentage = StatisticsUtils.getPercentage(total, li.value));

        const onOtherTooltipClicked = (key) => () => this.onItemClick(key);
        const onOtherTooltipHover = (_, e) => null;

        content = (
            <VeBoxCard className="dealers-widget__container home-dashboard-card">
                <VeBoxCard.Header
                    className="dealers-widget__header commercial-header space-between"
                    title={this.props.texts.title}
                    titleClassName={"lowercase capitalize"}
                    singleLineTitle={true}
                    back={
                        <WidgetBadge type={WidgetBadgeType.COMMERCIAL}/>
                    }
                />
                <VeBoxCard.Body className="dealers-widget__content">
                    <div className="cards-container">
                        <div className="top-card-container">
                            <CardBoxSmall
                                title={this.props.texts.equipmentWithTelemetry}
                                value={FormatLocaleNumber(this.state.cardsData.equipmentTotal)}
                                bolder={true}
                                extended={true}
                                onClick={this.onCardClick(
                                    ...REGISTERED_LOCATIONS_IDS,
                                    SiteTypes.UNREGISTERED_SITE,
                                    SiteTypes.WAREHOUSE)}
                            />
                        </div>
                        <div className="bottom-cards-container">
                            <CardBoxSmall
                                title={this.props.texts.equipmentOnWarehouse}
                                extended={true}
                                value={
                                    AssetBaseDashboardWidget.getValueWithPercentage(
                                        this.state.cardsData.newEquipmentCount,
                                        this.state.cardsData.equipmentTotal,
                                    )
                                }
                                onClick={this.onCardClick(SiteTypes.WAREHOUSE)}
                            />
                            <CardBoxSmall
                                title={this.props.texts.equipmentOnMarket}
                                extended={true}
                                value={
                                    AssetBaseDashboardWidget.getValueWithPercentage(
                                        this.state.cardsData.onMarketEquipmentCount,
                                        this.state.cardsData.equipmentTotal,
                                    )
                                }
                                onClick={this.onCardClick(
                                    ...REGISTERED_LOCATIONS_IDS,
                                    SiteTypes.UNREGISTERED_SITE)}
                            />
                        </div>
                    </div>

                    <Title title={this.props.texts.equipmentByFamily}/>
                    <div className="chart-container">
                        <div className="pie-chart__legend__container">
                            <Legend
                                items={legendItems}
                                selectedItemName={this.state.selectedItemName}
                                onItemHover={this.setSelectedItem}
                                onClick={this.onItemClick}
                                iconOptions={{show: true}}
                                showValues={true}
                            />
                            <LegendTooltip
                                config={{
                                    ...this.state.tooltipConfig,
                                    data: equipmentByTypeData.filter((x, i) => i >= 5),
                                }}
                                className={"tooltip-wrapper"}
                                style={{
                                    color: "white",
                                }}
                            >
                                {(equipments: any[]) => (
                                    <div
                                        className={"tooltip-body"}
                                        style={{
                                            maxHeight: 200,
                                            overflow: "auto",
                                        }}
                                    >
                                        <ul className="legend">
                                            {equipments.map((equipment, i) => (
                                                <LegendLiElement
                                                    key={i}
                                                    item={{
                                                        name: equipment.label,
                                                        color: legendItems[legendItems.length - 1].color,
                                                        code: equipment.code,
                                                        icon: equipment.icon,
                                                        value: equipment.frequency,
                                                        percentage: StatisticsUtils.getPercentage(
                                                            total,
                                                            equipment.frequency,
                                                        ),
                                                    }}
                                                    iconOptions={{show: true}}
                                                    onClick={onOtherTooltipClicked(equipment.label)}
                                                    showValues={true}
                                                    liStyle={{
                                                        color: "white",
                                                    }}
                                                    onHover={onOtherTooltipHover}
                                                />
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </LegendTooltip>
                        </div>
                        <div className="pie-chart__graph">
                            <AutoSizer style={{width: "auto"}}>
                                            {({width, height}) =>
                                                <PieChartWithLabels
                                                    donutOptions={
                                                        {activeDonut: true, showTotal: true}
                                                    }
                                                    width={width - 40}
                                                    height={height}
                                                    radiusRatio={2}
                                                    data={preparedData}
                                                    showPercentage={true}
                                                    showIcon={false}
                                                    noDataMessage={this.props.texts.noData}
                                                    iconSize={{width: 30, height: 30}}
                                                    labelLineDefaultLength={10}
                                                    order={"desc"}
                                                    selectedItem={this.state.selectedItemName}
                                                    labelsOptions={{externalLegend: true}}
                                                    onPathOver={this.setSelectedItem}
                                                    onClick={this.onSliceClicked}
                                                />}
                            </AutoSizer>
                        </div>
                    </div>
                </VeBoxCard.Body>
            </VeBoxCard>
        );
        return (
            <div className="dealers-widget">
                {content}
            </div>
        );
    }

    private onItemClick = (key): void => {
        const result = this.state.equipmentByType.find((x) => x.typeName === key);
        (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
            equipmentType: [result.typeGuid],
            sites: [
                ...REGISTERED_LOCATIONS_IDS,
                SiteTypes.UNREGISTERED_SITE,
                SiteTypes.WAREHOUSE],
            allTelemetries: this.props.withTelemetryDevice,
            applyFilter: true,
        });
    }

    private onCardClick = (...sites: SiteTypes[]) => {
        return () => {
            (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
                sites,
                allTelemetries: this.props.withTelemetryDevice,
                applyFilter: true,
            });
        };
    }

    private onSliceClicked = (d) => {
        (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
            equipmentType: [d.data.code],
            sites: [
                ...REGISTERED_LOCATIONS_IDS,
                SiteTypes.UNREGISTERED_SITE,
                SiteTypes.WAREHOUSE],
            allTelemetries: this.props.withTelemetryDevice,
            applyFilter: true,
        });
    }
}
